import { resolveComponent } from 'vue'
import { App } from './App'
import { useAppConfigs, useAppPages } from './config/use-config'

import { _, formatText, resolveVueAsset, flattenTree } from '../utils'
import { mergeLoaders } from './loaders'
import { initalizeApis, normalizeWidgetName } from './_internal/util'

import type { PageSchema, Widget } from '@zto/zpage-core'
import type { Handler } from 'mitt'
import type {
  IApp,
  AppConfig,
  MetaAppCtorOptions,
  AppEnv,
  AppApis,
  AppLoader,
  TextFormatters,
  FormatTextOptions,
  MetaAppInstallFunction,
  VueComponent,
  AppExContext,
  MicroAppConfig,
  PageContext,
  AppEventListener,
  AppAuthLoader
} from '../typings'

import { reloadModuleRoutes } from './router/util'
import { AppLoaderType } from '../consts'

/**
 * 元应用为嵌入在主应用中的应用
 * 此应用继承主应用部分特征，并保留当前应用独有的部分功能，以此与其他元应用分开
 */
export class MetaApp implements IApp {
  readonly mainApp: App // 主应用

  readonly name: string
  readonly env: AppEnv

  readonly config: AppConfig
  readonly exContext: AppExContext

  readonly apis: AppApis
  readonly pages: PageSchema[]
  readonly loaders: AppLoader[]
  readonly formatters: TextFormatters

  readonly components: Record<string, VueComponent>
  readonly widgets: Record<string, Widget>

  private _install?: MetaAppInstallFunction
  private _onLoad?: Function
  private _onUnload?: Function

  constructor(mainApp: App, opts: MetaAppCtorOptions) {
    // 强制设置当前应用为元应用，以便保证应用正确初始化
    mainApp.micro._setActiveApp(this)

    this.mainApp = mainApp

    this.name = opts.name

    this._install = opts.install

    // 合并环境变量
    const envMap = opts.envMap || {}
    const env = _.deepMerge(mainApp._env, envMap[mainApp._env.name]?.ENV, opts.env)
    this.env = Object.freeze(env)
    this.exContext = Object.freeze({ ...opts.exContext })

    // 初始化应用配置
    const config = useAppConfigs(mainApp, [opts.config])
    this._onLoad = config.app?.onLoad
    this._onUnload = config.app?.onUnload

    this.config = Object.freeze(_.deepMerge(mainApp._config, config))

    this.apis = this._initApis(config)

    this.pages = useAppPages(mainApp, opts.pages)

    const exts = opts.extensions || {}

    // 加载器
    this.loaders = mergeLoaders(mainApp._loaders, exts.loaders)

    // 自定义格式化
    this.formatters = Object.freeze({ ...mainApp._formatters, ...exts.formatters })

    // 安装全局组件
    this.components = (exts.components || []).reduce((acc, cur) => {
      if (cur.name) acc[cur.name] = cur
      return acc
    }, {} as Record<string, VueComponent>)

    this.widgets = (exts.widgets || []).reduce((acc, cur) => {
      acc[cur.name] = cur
      return acc
    }, {})

    mainApp.micro.checkActiveApp()
  }

  // 初始化api
  private _initApis(config: AppConfig): AppApis {
    const baseCfg = this.config.api
    const apiCfgs = config.apis

    const _apis = initalizeApis(apiCfgs, baseCfg)
    const apis = { ...this.mainApp._apis, ..._apis }

    return apis
  }

  get api() {
    return this.apis.appApi
  }

  get request() {
    return this.api.request
  }

  get ui() {
    return this.mainApp.ui
  }

  get emitter() {
    return this.mainApp.emitter
  }

  get stores() {
    return this.mainApp.stores
  }

  get storage() {
    return this.mainApp.storage
  }

  get router() {
    return this.mainApp.router
  }

  /** 获取组件 */
  resolveComponent(name: string) {
    let c = resolveVueAsset(this.components, name)
    if (!c) c = resolveComponent(name)
    return c
  }

  /** 获取微件 */
  resolveWidget(name: string) {
    let wName = normalizeWidgetName(name)

    let w = resolveVueAsset(this.widgets, wName)
    if (!w) w = resolveComponent(wName)

    return w
  }

  /**
   * 启用应用
   */
  async start(cfg: MicroAppConfig) {
    // 如果是单独配置应用，则初始化当前模块菜单
    if (cfg.singleModule === true) {
      await this._loadSingleModule(cfg.name)
    }

    // 安装元应用
    if (this._install) await this._install(this)

    if (this._onLoad) await Promise.resolve().then(() => this._onLoad!(this))

    return this
  }

  /** 暂停元应用 */
  async stop() {
    if (this._onUnload) await Promise.resolve().then(() => this._onUnload!(this))
  }

  /** 加载单独模块 */
  private async _loadSingleModule(moduleName: string) {
    const { appStore, userStore } = this.mainApp.stores

    const targetModule = appStore.submodules.find(it => it.name === moduleName)
    if (!targetModule) return

    // 清理原菜单并重建
    const authLoader = this.useAuthLoader()
    if (!authLoader) return

    // 元应用动态加载菜单
    if (authLoader.getMetaUserData) {
      const userData = await authLoader.getMetaUserData(this)
      let { menus, permissions } = userData || {}

      /** TODO: 后续可以通过数据隔离的方式更安全的管理第三方应用 */

      /** 为菜单名加前缀，防止命名冲突 */
      const allMenus = flattenTree(menus || [])
      allMenus.forEach(it => {
        it.name = `${moduleName}__${it.name}`
        if (it.keyName) it.keyName = `${moduleName}__${it.keyName}`
        if (it.parentName) it.parentName = `${moduleName}__${it.parentName}`

        // if (it.path) {
        //   it.path = it.path.startsWith('/') ? `${moduleName}${it.path}` : `${moduleName}/${it.path}`
        // }
      })

      if (!targetModule.children?.length) {
        await appStore.reloadModule(moduleName, {
          singleModule: true,
          children: menus
        })

        const baseRoute = this.mainApp.useConfig('router.base', '')
        await reloadModuleRoutes(this.mainApp.router, targetModule, baseRoute)
      }

      /** 为权限加前缀，防止命名冲突 */
      permissions = (permissions || []).map(p => `${moduleName}__${p}`)

      await userStore.patchPermissions(permissions || [])
    }
  }

  emits(eTypes: string[] | string, payload?: any) {
    return this.mainApp.emits(eTypes, payload)
  }

  ons(eTypes: AppEventListener, handler: Handler<unknown> | undefined) {
    return this.mainApp.ons(eTypes, handler)
  }

  offs(eTypes: string[] | string, handler: Handler<unknown> | undefined) {
    return this.mainApp.offs(eTypes, handler)
  }

  useContext(data: any = {}): PageContext {
    return this.mainApp.useContext(data)
  }

  useAppConfig(path?: string, defaultValue?: unknown) {
    const _path = path ? `.${path}` : ''
    return this.useConfig(`app${_path}`, defaultValue)
  }

  useConfig(path: string, defaultValue?: unknown) {
    const cfg = _.get(this.config, path, defaultValue)
    return cfg
  }

  checkPermission(codes: string | string[]) {
    return this.mainApp.checkPermission(codes)
  }

  filter(target: any, data: any = {}) {
    return this.mainApp.filter(target, data)
  }

  deepFilter(target: any, data: any = {}) {
    return this.mainApp.deepFilter(target, data)
  }

  useRoute() {
    return this.mainApp.useRoute()
  }

  /** 获取auth loader, 默认获取当前配置的loader */
  useAuthLoader(name?: string): AppAuthLoader | undefined {
    if (!name) name = this.useAppConfig('auth', {}).loader
    if (!name) return undefined

    return this.useLoader<AppAuthLoader>(AppLoaderType.AUTH, name)
  }

  /** 获取loader */
  useLoader<T extends AppLoader>(type: AppLoaderType, name: string): T | undefined {
    if (!type || !name) return undefined

    if (typeof name === 'object') return name

    const _loader = this.loaders.find(it => it.type === type && it.name === name)
    return _loader as T | undefined
  }

  // 格式化
  formatText(val: any, f: string | Function | Record<string, any>, options?: FormatTextOptions) {
    const opts = options || {}
    opts.formatters = { ...this.formatters, ...opts.formatters }

    return formatText(val, f, opts)
  }
}
